import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PlusIcon, SettingsIcon } from 'lucide-react';

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox';
import type { LabelView } from '@/lib/api/generated/model';

import { matchLabelsByName, normalizeForCompare } from '../labelName';

export interface LabelPickerProps {
  /** The workspace's whole vocabulary, including labels no task carries. */
  registry: readonly LabelView[];
  /** Currently selected label identifiers. */
  selected: readonly string[];
  onChange: (ids: string[]) => void;
  /** Create a label that does not exist yet, and return it. */
  onCreate: (name: string) => Promise<LabelView>;
  placeholder?: string;
  /** Set where a `<label>` names this control, so the two can be tied together. */
  id?: string;
  /**
   * The workspace whose settings hold the vocabulary, for the way out at the
   * foot of the list. Optional: a caller with no workspace in hand — a test,
   * or a surface that is not inside one — gets the picker without the link
   * rather than a link that goes nowhere.
   */
  workspaceId?: string;
  /**
   * `field` (the default) is a search box, right in a form like the create
   * dialog's. `button` is a small "Add label" chip that opens the search (spec
   * 181): beside a task's chips in the drawer, an always-empty box under them
   * read as a second, blank field.
   */
  trigger?: 'field' | 'button';
}

/**
 * Choose a task's labels, creating one along the way if the word you want is
 * not in the vocabulary yet.
 *
 * The create row is not a convenience. A vocabulary that can only be filled from
 * a settings page stays empty, and everyone quietly goes back to not labelling —
 * so the moment someone types a word that means something to them is the moment
 * to offer to keep it.
 *
 * Selection is by identifier rather than by name throughout: a rename must not
 * silently change what a task carries.
 *
 * The foot of the list is the way back to the vocabulary itself. Labels are
 * born here and can only be renamed, recoloured or deleted in Settings, and
 * nothing said so — so a workspace accumulates `acme`, `Acme` and `acme-corp`,
 * and the one surface where anybody would notice had no exit toward the fix.
 */
export function LabelPicker({
  registry,
  selected,
  onChange,
  onCreate,
  placeholder = 'Add a label…',
  id,
  workspaceId,
  trigger = 'field',
}: LabelPickerProps) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const matches = useMemo(
    () => matchLabelsByName(registry, query),
    [registry, query],
  );

  // Only offer to create when the typed name is not already a label. The
  // comparison is the same case-insensitive one the backend enforces, so the
  // picker never offers a create the API would refuse as a conflict.
  const trimmed = query.trim();
  const canCreate =
    trimmed.length > 0 &&
    !registry.some(
      (label) =>
        normalizeForCompare(label.name) === normalizeForCompare(trimmed),
    );

  async function create() {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await onCreate(trimmed);
      onChange([...selected, created.id]);
      setQuery('');
    } catch (error) {
      // The refusal is classified by `useCreateLabel` before it gets here, so
      // this is the sentence to show, not a status to interpret. The query is
      // left standing: a name refused as taken is one the reader has to edit,
      // and clearing the field would take away the thing they were editing.
      setCreateError(
        error instanceof Error
          ? error.message
          : 'The label could not be created.',
      );
    } finally {
      setCreating(false);
    }
  }

  /** The search field: in the form for `field`, at the top of the list for
   *  `button`. */
  function search(className: string | undefined) {
    return (
      <ComboboxInputGroup className={className}>
        <ComboboxInput
          id={trigger === 'field' ? id : undefined}
          aria-label={
            trigger === 'button' ? 'Find or create a label' : undefined
          }
          placeholder={
            trigger === 'button' ? 'Find or create a label…' : placeholder
          }
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            // The refusal was about the name that was typed. Editing it makes
            // the message stale, and a stale refusal under a fresh name reads
            // as a refusal of the fresh one.
            setCreateError(null);
          }}
        />
      </ComboboxInputGroup>
    );
  }

  return (
    <Combobox<string, true>
      multiple
      value={[...selected]}
      onValueChange={(ids) => {
        onChange(ids);
      }}
      items={matches.map((label) => label.id)}
      // The items are identifiers; the option text is the name. Without this,
      // Base UI's own filtering and typeahead compare what the reader types
      // against a UUID and match nothing — invisible today only because this
      // picker filters into `matches` itself and renders static children rather
      // than using `ComboboxEmpty`, and because the unit fixtures happen to use
      // `id === name`. Naming them here keeps that accident from mattering.
      itemToStringLabel={(id) =>
        registry.find((label) => label.id === id)?.name ?? id
      }
      onOpenChange={(open) => {
        if (!open && trigger === 'button') {
          setQuery('');
          setCreateError(null);
        }
      }}
    >
      {trigger === 'field' ? search(undefined) : null}
      {trigger === 'button' ? (
        <ComboboxTrigger
          id={id}
          aria-label="Add a label"
          className="border-border text-muted-foreground hover:text-foreground hover:bg-accent/60 focus-visible:ring-ring inline-flex h-7 items-center gap-1 rounded-full border border-dashed px-2.5 text-xs pointer-coarse:h-11 transition-colors focus-visible:ring-3 focus-visible:outline-none"
        >
          <PlusIcon aria-hidden="true" className="size-3" />
          Add label
        </ComboboxTrigger>
      ) : null}

      <ComboboxContent
        className={trigger === 'button' ? 'w-auto min-w-64' : undefined}
      >
        {trigger === 'button' ? search('mb-1') : null}
        <ComboboxList>
          {matches.map((label) => (
            <ComboboxItem key={label.id} value={label.id}>
              {label.name}
            </ComboboxItem>
          ))}
        </ComboboxList>

        {canCreate && (
          // Deliberately *not* `ComboboxEmpty`. That slot renders only when the
          // list matched nothing, but "acm" matches `acme` and is still a name
          // someone may mean to keep — offering to create only when nothing
          // matches would hide the row exactly when the vocabulary is growing
          // around a common prefix.
          <div className="border-border shrink-0 border-t pt-1">
            <button
              type="button"
              disabled={creating}
              onClick={() => void create()}
              aria-describedby={
                createError ? 'label-picker-create-error' : undefined
              }
              className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm focus-visible:ring-3 focus-visible:outline-none"
            >
              <PlusIcon className="size-3.5 shrink-0" />
              Create “{trimmed}”
            </button>
            {createError ? (
              // In the create row itself rather than at the foot of the popover:
              // the refusal is about the act this row offers, and a message
              // parked elsewhere gets read as being about the list.
              <p
                id="label-picker-create-error"
                role="alert"
                className="text-destructive px-2 pt-1 pb-0.5 text-xs"
              >
                {createError}
              </p>
            ) : null}
          </div>
        )}

        {workspaceId ? (
          <Link
            to="/workspaces/$workspaceId/settings"
            params={{ workspaceId }}
            hash="labels-heading"
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex w-full shrink-0 items-center gap-2 rounded-md px-2 py-1.5 text-xs focus-visible:ring-3 focus-visible:outline-none"
          >
            <SettingsIcon aria-hidden="true" className="size-3.5 shrink-0" />
            Manage labels
          </Link>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}
